/* eslint-disable react/react-in-jsx-scope */
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";

const DropdownFilterType = ({
  options,
  name,
  value,
  slug,
  handleChange,
}: {
  options: any[];
  name: string;
  value: string[];
  slug: string;
  handleChange: (slug: string, value: string[]) => void;
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(value);

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  const handleBlur = () => {
    const hasChanged =
      selectedValues.length !== value.length ||
      !selectedValues.every((val) => value.includes(val));

    if (hasChanged) {
      handleChange(slug, selectedValues);
    }
  };

  const handleCheckboxChange = (optionValue: string, isChecked: boolean) => {
    setSelectedValues((prev) => {
      if (isChecked) {
        if (!prev.includes(optionValue)) {
          return [...prev, optionValue];
        }
      } else {
        return prev.filter((item) => item !== optionValue);
      }
      return prev;
    });
  };

  const allSelected = options.every((opt) =>
    selectedValues.includes(opt.value)
  );
  const toggleAll = (checked: boolean) => {
    if (checked) {
      const all = options.map((opt) => opt.value);
      setSelectedValues(all);
      handleChange(slug, all);
    } else {
      setSelectedValues([]);
      handleChange(slug, []);
    }
  };

  return (
    <div className="relative">
      <DropdownMenu onOpenChange={(open) => !open && handleBlur()}>
        <DropdownMenuTrigger asChild>
          <button className="text-xs py-[3px] h-[26px] border  rounded-md px-3 font-normal flex gap-1 items-center">
            {name}
            <ChevronDown size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="max-h-64 overflow-auto transition-all"
        >
          <DropdownMenuLabel className="flex justify-between">
            <div className="flex justify-between w-full">
              <div className="flex gap-1 items-center">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  id={name}
                />
                <label htmlFor={name}>{name}</label>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map(
            (option, i) =>
              option.label && (
                <FilterItem
                  key={i}
                  option={option}
                  selectedValues={selectedValues}
                  handleCheckboxChange={handleCheckboxChange}
                />
              )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const FilterItem = ({ option, selectedValues, handleCheckboxChange }) => {
  const [isChildrenVisible, setIsChildrenVisible] = useState(false);
  return (
    <div className="flex items-start ">
      {option?.children && option?.children?.length > 0 && (
        <button
          className="size-4 flex justify-center items-center bg-gray-50 rounded-sm mt-2"
          onClick={() => setIsChildrenVisible(!isChildrenVisible)}
        >
          {!isChildrenVisible ? (
            <span className="w-0 h-0 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-black inline-block"></span>
          ) : (
            <span className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-black inline-block"></span>
          )}
        </button>
      )}
      <div className="flex flex-col">
        <DropdownMenuCheckboxItem
          checked={selectedValues.includes(option.value)}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={(checked) =>
            handleCheckboxChange(option.value, checked)
          }
          className="flex items-center gap-2 w-full"
        >
          {option.icon} {option.label}
        </DropdownMenuCheckboxItem>
        {option.children && option.children.length > 0 && isChildrenVisible && (
          <div className="flex flex-col ml-4">
            {option.children.map((item, key) => (
              <DropdownMenuCheckboxItem
                key={key}
                checked={selectedValues.includes(item.value)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(checked) =>
                  handleCheckboxChange(item.value, checked)
                }
                className="flex items-center gap-2 w-full"
              >
                {item.icon} {item.label}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default DropdownFilterType;
